import React, { useState } from 'react'
import { Modal } from '@/shared/components/ui'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { DateRangeFilter } from '@/shared/components/ui'
import { SavedFiltersManager } from '@/shared/components/entities'

export interface FilterField {
  key: string
  label: string
  type: 'text' | 'select' | 'date' | 'daterange' | 'boolean' | 'number'
  options?: { value: string; label: string }[]
  placeholder?: string
}

export interface FilterValues {
  [key: string]: any
}

export interface SavedFilter {
  id: string
  name: string
  filters: FilterValues
  created_at: string
  is_default?: boolean
}

interface AdvancedFiltersModalProps {
  fields: FilterField[]
  values: FilterValues
  onChange: (values: FilterValues) => void
  onSave?: (name: string, isDefault: boolean) => Promise<void>
  onLoad?: (filterId: string) => Promise<void>
  onDelete?: (filterId: string) => Promise<void>
  savedFilters?: SavedFilter[]
  loading?: boolean
  
  // Display mode
  iconOnly?: boolean  // If true, shows only icon without text (for search bar integration)
  
  // External control (optional)
  isOpen?: boolean
  onClose?: () => void
  
  // Labels i18n
  labels?: {
    buttonLabel?: string
    title?: string
    clearAll?: string
    apply?: string
    saveFilter?: string
    loadFilter?: string
    activeFilters?: string
  }
  // Optional translation namespace for route-specific translations
  namespace?: string
}

export function AdvancedFiltersModal({
  fields,
  values,
  onChange,
  onSave,
  onLoad,
  onDelete,
  savedFilters = [],
  loading = false,
  iconOnly = false,
  isOpen: externalIsOpen,
  onClose: externalOnClose,
  labels = {}
  , namespace
}: AdvancedFiltersModalProps) {
  const { t } = useTranslation()
  const [internalIsOpen, setInternalIsOpen] = useState(false)
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  
  // Use external control if provided, otherwise use internal state
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen
  const setIsOpen = externalOnClose !== undefined 
    ? (value: boolean) => { if (!value) externalOnClose() }
    : setInternalIsOpen
  
  const {
    buttonLabel = t('list.quickFilters.advancedTitle'),
    title = t('list.quickFilters.advancedTitle'),
    clearAll = t('list.quickFilters.clearAll'),
    apply = t('list.quickFilters.apply'),
    saveFilter = t('list.quickFilters.saveFilterButton'),
    loadFilter = t('list.quickFilters.savedFiltersLabel'),
    activeFilters = 'filtros ativos'
  } = labels

  // Count active filters
  const activeFiltersCount = Object.keys(values).filter(key => {
    const value = values[key]
    if (value === null || value === undefined || value === '') return false
    if (Array.isArray(value) && value.length === 0) return false
    if (typeof value === 'object' && !Array.isArray(value)) {
      return Object.values(value).some(v => v !== null && v !== undefined && v !== '')
    }
    return true
  }).length

  const handleFieldChange = (key: string, value: any) => {
    onChange({ ...values, [key]: value })
  }

  const handleClearAll = () => {
    const clearedValues: FilterValues = {}
    fields.forEach(field => {
      if (field.type === 'daterange') {
        clearedValues[field.key] = { start: null, end: null }
      } else if (field.type === 'boolean') {
        clearedValues[field.key] = null
      } else {
        clearedValues[field.key] = ''
      }
    })
    onChange(clearedValues)
  }

  const translateFieldLabel = (key: string, defaultLabel?: string) => {
    if (!namespace) return defaultLabel || key

    const toCamel = (s: string) => s.replace(/_([a-z])/g, (_, c) => c.toUpperCase())
    const toSnake = (s: string) => s.replace(/([A-Z])/g, '_$1').toLowerCase()

    const base = key.replace(/_id$/, '')
    const variants = Array.from(new Set([key, toCamel(key), toSnake(key), base, toCamel(base), toSnake(base)]))

    for (const vKey of variants) {
      const tryKeys = [
        `${namespace}.record.fields.${vKey}`,
        `${namespace}.record.fields.${vKey}.label`,
        `${namespace}.list.columns.${vKey}.title`,
        `${namespace}.list.columns.${vKey}.label`,
        `${namespace}.list.columns.${vKey}`,
        `${namespace}.fields.${vKey}`,
        `${namespace}.columns.${vKey}`,
      ]
      for (const k of tryKeys) {
        const v = t(k)
        if (v && v !== k) return v
      }
    }

    return defaultLabel || key
  }

  const translatePlaceholder = (key: string, defaultPlaceholder?: string) => {
    if (!namespace) return defaultPlaceholder || ''

    const toCamel = (s: string) => s.replace(/_([a-z])/g, (_, c) => c.toUpperCase())
    const toSnake = (s: string) => s.replace(/([A-Z])/g, '_$1').toLowerCase()
    const variants = Array.from(new Set([key, toCamel(key), toSnake(key)]))

    const tryGlobal = [
      `${namespace}.filters.searchPlaceholder`,
    ]
    for (const gk of tryGlobal) {
      const gv = t(gk)
      if (gv && gv !== gk) return gv
    }

    for (const vKey of variants) {
      const tryKeys = [
        `${namespace}.record.fields.${vKey}.placeholder`,
        `${namespace}.record.fields.${vKey}Placeholder`,
        `${namespace}.record.fields.${vKey}.help`,
        `${namespace}.record.fields.${vKey}`,
      ]
      for (const k of tryKeys) {
        const v = t(k)
        if (v && v !== k) return v
      }
    }

    return defaultPlaceholder || ''
  }

  const booleanLabels = (key: string) => {
    const all = namespace ? t(`${namespace}.filters.all`) : ''
    const yes = namespace ? t(`${namespace}.record.fields.${key}.options.true`) : ''
    const no = namespace ? t(`${namespace}.record.fields.${key}.options.false`) : ''

    const allLabel = all && all !== `${namespace}.filters.all` ? all : (t('list.quickFilters.boolean.all') || 'Todos')
    const yesLabel = yes && yes !== `${namespace}.record.fields.${key}.options.true` ? yes : (t('list.quickFilters.boolean.yes') || 'Sim')
    const noLabel = no && no !== `${namespace}.record.fields.${key}.options.false` ? no : (t('list.quickFilters.boolean.no') || 'Não')

    return { allLabel, yesLabel, noLabel }
  }

  const renderField = (field: FilterField) => {
    const value = values[field.key]
    

    switch (field.type) {
      case 'text':
      case 'number':
        return (
            <input
              type={field.type}
              value={value || ''}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              placeholder={translatePlaceholder(field.key, field.placeholder)}
              aria-label={translateFieldLabel(field.key, field.label)}
              className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400"
            />
        )

      case 'select':
        return (
            <select
              value={value || ''}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              aria-label={translateFieldLabel(field.key, field.label)}
              className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400"
            >
              <option value="">{translatePlaceholder(field.key, field.placeholder) || 'Selecione...'}</option>
              {field.options?.map(option => {
                const optLabel = namespace ? t(`${namespace}.record.fields.${field.key}.options.${option.value}`) : ''
                return (
                  <option key={option.value} value={option.value}>
                    {optLabel && optLabel !== `${namespace}.record.fields.${field.key}.options.${option.value}` ? optLabel : option.label}
                  </option>
                )
              })}
            </select>
        )

      case 'date':
        return (
          <input
            type="date"
            value={value || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400"
          />
        )

      case 'daterange':
        return (
          <DateRangeFilter
            value={value || { start: null, end: null }}
            onChange={(range) => handleFieldChange(field.key, range)}
          />
        )

      case 'boolean':
        return (
          <div className="flex items-center gap-3">
            {(() => {
              const { allLabel, yesLabel, noLabel } = booleanLabels(field.key)
              return (
                <>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name={field.key}
                      checked={value === null || value === undefined || value === ''}
                      onChange={() => handleFieldChange(field.key, null)}
                      className="text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">{allLabel}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name={field.key}
                      checked={value === true}
                      onChange={() => handleFieldChange(field.key, true)}
                      className="text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">{yesLabel}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name={field.key}
                      checked={value === false}
                      onChange={() => handleFieldChange(field.key, false)}
                      className="text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">{noLabel}</span>
                  </label>
                  <button
                    onClick={() => handleFieldChange(field.key, null)}
                    className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                  >
                    {t('list.quickFilters.clear')}
                  </button>
                </>
              )
            })()}
          </div>
        )

      default:
        return null
    }
  }

  return (
    <>
      {/* Only render button if not externally controlled */}
      {externalIsOpen === undefined && (
        <>
          {iconOnly ? (
            // Icon-only version for search bar
            <button
              onClick={() => setIsOpen(true)}
              className="p-1 text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors shrink-0 relative"
              title={buttonLabel}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              {activeFiltersCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white text-[10px] font-bold">
                  {activeFiltersCount}
                </span>
              )}
            </button>
          ) : (
            // Full button version
            <button
              onClick={() => setIsOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              {buttonLabel}
              {activeFiltersCount > 0 && (
                <span className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-xs rounded-full font-semibold">
                  {activeFiltersCount}
                </span>
              )}
            </button>
          )}
        </>
      )}

      {/* Modal */}
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={title}
        size="lg"
        onSubmit={() => setIsOpen(false)}
        submitLabel={apply}
      >
        <div className="space-y-6">
          {/* Header Actions */}
          <div className="flex items-center justify-between pb-4 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-2">
              {savedFilters.length > 0 && onLoad && (
                <SavedFiltersManager
                  savedFilters={savedFilters}
                  onLoad={async (filterId) => {
                    await onLoad(filterId)
                  }}
                  onDelete={onDelete}
                  loading={loading}
                />
              )}
              
              {onSave && activeFiltersCount > 0 && (
                <button
                  onClick={() => setShowSaveDialog(true)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
                  title={saveFilter}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                  {saveFilter}
                </button>
              )}
            </div>

            {activeFiltersCount > 0 && (
              <button
                onClick={handleClearAll}
                className="px-3 py-1.5 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                {clearAll}
              </button>
            )}
          </div>

          {/* Filters Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto px-1">
            {fields.map(field => (
              <div key={field.key} className={field.type === 'daterange' ? 'md:col-span-2' : ''}>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                  {translateFieldLabel(field.key, field.label)}
                </label>
                {renderField(field)}
              </div>
            ))}
          </div>

          {/* Actions are handled by Modal header (convention) */}
        </div>

        {/* Save Filter Dialog */}
        {showSaveDialog && onSave && (
          <SaveFilterDialog
            onSave={async (name, isDefault) => {
              await onSave(name, isDefault)
              setShowSaveDialog(false)
            }}
            onCancel={() => setShowSaveDialog(false)}
          />
        )}
      </Modal>
    </>
  )
}

// Dialog para salvar filtro
function SaveFilterDialog({
  onSave,
  onCancel
}: {
  onSave: (name: string, isDefault: boolean) => Promise<void>
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [isDefault, setIsDefault] = useState(false)
  const [saving, setSaving] = useState(false)

  const submit = async () => {
    if (!name.trim()) return
    setSaving(true)
    try {
      await onSave(name.trim(), isDefault)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen onClose={onCancel} title={t('list.quickFilters.saveDialog.title')} onSubmit={submit} submitLabel={t('list.quickFilters.saveDialog.saveButton')} submitting={saving}>
      <form onSubmit={(e) => { e.preventDefault(); submit() }}>
        <div className="p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                {t('list.quickFilters.saveDialog.nameLabel')}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('list.quickFilters.saveDialog.namePlaceholder')}
                className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                autoFocus
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="rounded text-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-sm text-slate-700 dark:text-slate-300">
                {t('list.quickFilters.saveDialog.defaultLabel')}
              </span>
            </label>
          </div>
        </div>
      </form>
    </Modal>
  )
}
